import { CommonModule } from '@angular/common';
import { Component, computed, DestroyRef, inject, OnInit, resource, signal } from '@angular/core';
import { rxResource, takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
    AbstractControl,
    FormArray,
    FormBuilder,
    FormControl,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    ValidationErrors,
    ValidatorFn,
    Validators
} from '@angular/forms';
import { provideLuxonDateAdapter } from '@angular/material-luxon-adapter';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxChange, MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmDialogComponent } from '../../dialogs/confirm-dialog/confirm-dialog.component';
import { DateTime } from 'luxon';
import { debounceTime, delay, distinctUntilChanged, filter, firstValueFrom, map, of, skip } from 'rxjs';
import { AdvancedUsersOnlyDirective } from "../../../directives/advanced-users-only.directive";
import { UrlNormalizerDirective } from '../../../directives/url-normalizer.directive';
import { Affiliation } from '../../../models/affiliation';
import { Contact } from '../../../models/contact';
import { LinkedInStatus, OngoingRelationship, PhoneType, SupplierFeedback, ThreeLevelScale } from '../../../models/enums';
import { Initiative } from '../../../models/initiative';
import { OrganisationSummary } from '../../../models/organisation-summary';
import { SimilarityList } from '../../../models/similarity-list';
import { LastUpdatePipe } from '../../../pipes/last-update.pipe';
import { SimilarityListPipe } from '../../../pipes/similarity-list.pipe';
import { ContactService, ContactSimilarityCheckPayload } from '../../../services/contact.service';
import { EventService } from '../../../services/event.service';
import { InitiativeService } from '../../../services/initiative.service';
import { O365AuthService, ROLES } from '../../../services/o365-auth.service';
import { OrganisationService } from '../../../services/organisation.service';
import { ProjectService } from '../../../services/project.service';
import { ReferenceDataService } from '../../../services/reference-data.service';
import { MY_DATE_FORMATS, urlValidator } from '../../../utils/utils';
import { AdvancedChipSelectComponent } from '../../misc/advanced-chip-select/advanced-chip-select.component';
import { separatorKeysCodes } from '../../misc/nested-options';
import { createPhoneNumberFormGroup, PhoneNumberComponent } from "../../misc/phone-number/phone-number.component";
import { createPostalAddressFormGroup, PostalAddressComponent } from '../../misc/postal-address/postal-address.component';
import { SearchableSelectComponent } from '../../misc/searchable-select/searchable-select.component';
import { HistoryPanelComponent } from "../../misc/history-panel/history-panel.component";
import { MatBadgeModule } from '@angular/material/badge';

export const uniqueOrganisationValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const formArray = control as FormArray;
    const selectedIds: number[] = [];
    let hasDuplicates = false;
    for (const group of formArray.controls) {
        const orgId = group.get('organisation')?.value?.id;  // contains the id
        if (selectedIds.includes(orgId)) {
            hasDuplicates = true;
            break;
        }
        selectedIds.push(orgId);
    }
    return hasDuplicates ? { duplicateOrganisation: true } : null;
};

export const atLeastOneFilledValidator: ValidatorFn = (group: AbstractControl): ValidationErrors | null => {
    const position = group.get('position')?.value;
    const organisation = group.get('organisation')?.value;
    // Check if both are empty/null
    const isPositionEmpty = !position || position.trim() === '';
    const isOrganisationEmpty = !organisation;

    return (isPositionEmpty && isOrganisationEmpty) ? { rowEmpty: true } : null;
};

@Component({
    selector: 'app-edit-contact',
    imports: [
        AdvancedChipSelectComponent,
        CommonModule,
        FormsModule,
        LastUpdatePipe,
        MatAutocompleteModule,
        MatButtonModule,
        MatCardModule,
        MatCheckboxModule,
        MatDatepickerModule,
        MatExpansionModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        MatListModule,
        MatRadioModule,
        MatSelectModule,
        MatTabsModule,
        MatTooltipModule,
        PostalAddressComponent,
        ReactiveFormsModule,
        SearchableSelectComponent,
        UrlNormalizerDirective,
        PhoneNumberComponent,
        MatProgressSpinnerModule,
        SimilarityListPipe,
        HistoryPanelComponent,
        MatBadgeModule
    ],
    providers: [
        provideLuxonDateAdapter(),
        { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS },
        { provide: MAT_DATE_LOCALE, useValue: 'fr' },
    ],
    templateUrl: './edit-contact.component.html',
    styleUrl: './edit-contact.component.scss'
})
export class EditContactComponent implements OnInit {
    SupplierFeedback = SupplierFeedback;
    OngoingRelationship = OngoingRelationship;
    LinkedInStatus = LinkedInStatus;
    ThreeLevelScale = ThreeLevelScale;
    Validators = Validators;

    readonly additionalInfoMaxLen = 500;
    readonly _snackBar = inject(MatSnackBar);
    readonly separatorKeysCodes = separatorKeysCodes;
    commonDataFG: FormGroup;
    supplierDataFG: FormGroup;
    isSupplierCheckbox: FormControl;

    returnUrl: string = '/contacts';

    mode: string = 'Create'; // Create, Update or Display
    contact: Contact = new Contact();
    resetCounter = 0; // used to reset the filter form
    isSupplier = signal(false);

    private destroyRef = inject(DestroyRef);
    readonly refDataService = inject(ReferenceDataService);
    readonly contactService = inject(ContactService);
    readonly projectService = inject(ProjectService);
    readonly initiativeService = inject(InitiativeService);
    readonly eventService = inject(EventService);
    readonly organisationService = inject(OrganisationService);
    readonly authService = inject(O365AuthService);

    readonly allTopics = this.refDataService.getGeneralTopicsOfInterest();
    readonly allTopicOptions = computed(() => this.allTopics());
    readonly allStakeholderGroups = this.refDataService.getContactStakeholdeGroups();
    readonly allStakeholderGroupOptions = computed(() => this.allStakeholderGroups());
    readonly allProducts = this.refDataService.getProducts();
    readonly allProductOptions = computed(() => this.allProducts());

    readonly allProjects = toSignal(this.projectService.getAllProjects(), { initialValue: [] });
    readonly allProjectOptions = computed(() => this.allProjects());

    readonly organisations = resource<OrganisationSummary[], unknown>({
        loader: () => firstValueFrom(this.organisationService.getAllOrganisationsSummary()),
        defaultValue: []
    })
    readonly allOrganisationOptions = computed(() => this.organisations.value().map(v => (
        {
            id: v.id,
            name: v.name,
            alternateName: v.acronym ?? '',
            member: v.activeMember,
            supplier: v.supplier,
            icons: [
                ...(v.archived ? ['archive'] : []),
                ...(v.activeMember ? ['family_star'] : []),
                ...(v.supplier ? ['partner_exchange'] : []),]
        })));

    readonly initiatives = resource<Initiative[], unknown>({
        loader: () => firstValueFrom(this.initiativeService.getAllInitiatives()),
        defaultValue: []
    });
    readonly allInitiativeOptions = computed(() => this.initiatives.value())

    readonly allEvents = toSignal(this.eventService.getAllEvents(), { initialValue: [] });
    readonly allEventOptions = computed(() => this.allEvents().map(v => ({ id: v.id, name: v.name })));

    private checkDuplicateParams = signal<ContactSimilarityCheckPayload | undefined>(undefined);

    duplicateEntryResource = rxResource<SimilarityList<any> | null, ContactSimilarityCheckPayload | undefined>({
        params: this.checkDuplicateParams,
        stream: ({ params }) => {
            if (!params) {
                return of(null);
            }
            return this.contactService.checkForSimilarContacts(params).pipe(delay(250));;
        },
    });


    constructor(private readonly fb: FormBuilder, private readonly router: Router, private readonly route: ActivatedRoute,
        private readonly dialog: MatDialog,) {
        this.commonDataFG = {} as FormGroup;
        this.supplierDataFG = {} as FormGroup;
        this.isSupplierCheckbox = new FormControl('false');
    }

    ngOnInit(): void {
        this.buildForm();
        this.returnUrl =
            this.route.snapshot.queryParamMap.get('returnUrl') ?? '/contacts';
        const idParam = this.route.snapshot.paramMap.get('id');
        if (idParam === null) {
            this.mode = 'Create';
            this.initEmptyForm();
        } else {
            const contactId = Number(idParam);
            if (!idParam || isNaN(contactId)) {
                this.router.navigateByUrl(this.returnUrl, {
                    state: { message: '❌ No such contact: ' + idParam }
                });
                return;
            }
            this.mode = 'Update';
            this.contactService.getContactById(contactId).subscribe({
                next: (contact: Contact) => {
                    this.contact = contact;
                    console.debug('contact', this.contact);
                    this.affiliationsFA.clear();
                    contact.affiliations.forEach(item => {
                        this.addAffiliation(item);
                    })
                    this.commonDataFG.patchValue({
                        ...this.contact,
                        projects: this.contact.projects,
                        optOuts: {
                            communicationType1OptIn: this.contact.communicationType1OptIn,
                            communicationType2OptIn: this.contact.communicationType2OptIn,
                            communicationType3OptIn: this.contact.communicationType3OptIn,
                            communicationType4OptIn: this.contact.communicationType4OptIn,
                            communicationType5OptIn: this.contact.communicationType5OptIn,
                        },
                        phoneNumber: this.contact.phoneNumber ?? {
                            rawNumber: '',
                            e164: '',
                            countryIsoCode: '',
                            type: PhoneType.LANDLINE
                        },
                        postalAddress: this.contact.postalAddress ?? {
                            address1: '',
                            address2: '',
                            zipCode: '',
                            city: '',
                            country: null,
                            name: 'main'
                        },
                    })
                    if (contact.supplierDetails) {
                        this.isSupplier.set(true);
                        this.isSupplierCheckbox.setValue(true);
                        this.supplierDataFG.enable();
                        this.supplierDataFG.patchValue(contact.supplierDetails ?? {});
                        this.commonDataFG.get('stakeholderGroups')?.setValidators([]);
                        this.commonDataFG.get('stakeholderGroups')?.updateValueAndValidity();
                    } else {
                        this.isSupplier.set(false);
                        this.isSupplierCheckbox.setValue(false);
                        this.supplierDataFG.disable();
                        this.commonDataFG.get('stakeholderGroups')?.setValidators(Validators.required);
                        this.commonDataFG.get('stakeholderGroups')?.updateValueAndValidity();
                    }
                },
                error: (err: string) => {
                    console.error('Failed to load contact:', err);
                    this.router.navigateByUrl(this.returnUrl,
                        {
                            state: { message: '❌ Failed to load contact: ' + contactId }
                        }
                    );
                }
            })
        }
    }

    private buildForm(): void {
        // Admin users can save contacts without email
        // 20261012: no longer the case (see Teams chat): 
        // it would make sense to allow all types of users to save contacts without email
        // const email1Validators = this.authService.hasRole(ROLES.ADMIN)
        //     ? [Validators.email]
        //     : [Validators.required, Validators.email];
        const email1Validators = [Validators.email];

        this.commonDataFG = this.fb.group({
            firstName: [this.contact?.firstName || '', { validators: [Validators.required] }],
            lastName: [this.contact?.lastName || '', Validators.required],
            email1: [this.contact?.email1 || '', email1Validators],
            email2: [this.contact?.email2 || '', Validators.email],
            phoneNumber: createPhoneNumberFormGroup(false),
            optOuts: this.fb.group({
                communicationType1OptIn: [this.contact?.communicationType1OptIn || true],
                communicationType2OptIn: [this.contact?.communicationType2OptIn || true],
                communicationType3OptIn: [this.contact?.communicationType3OptIn || false],
                communicationType4OptIn: [this.contact?.communicationType4OptIn || true],
                communicationType5OptIn: [this.contact?.communicationType5OptIn || true],
            }),
            postalAddress: createPostalAddressFormGroup(false, true),
            linkedInProfile: [this.contact?.linkedInProfile || '', urlValidator()],
            linkedInStatus: [this.contact?.linkedInStatus || LinkedInStatus.NOT_CONNECTED],
            additionalInfo: [this.contact?.additionalInfo || '', Validators.maxLength(this.additionalInfoMaxLen)],
            topics: [],
            stakeholderGroups: this.fb.control([], Validators.required), // Required only if not a supplier
            projects: [],
            initiatives: [],
            events: [],
            affiliationsFA: this.fb.array([], uniqueOrganisationValidator),
            scientificAdvisoryBoard: [this.contact?.scientificAdvisoryBoard || false],
            boardOfDirectors: [this.contact?.boardOfDirectors || false],
            generalAssembly: [this.contact?.generalAssembly || false],
            executiveCommittee: [this.contact?.executiveCommittee || false],
            nominationCommittee: [this.contact?.nominationCommittee || false],
            ethicsCommittee: [this.contact?.ethicsCommittee || false]
        });


        if (!this.authService.hasAnyRole([ROLES.ADVANCED, ROLES.ADMIN])) {
            this.commonDataFG.get('linkedInProfile')?.disable();
            this.commonDataFG.get('linkedInStatus')?.disable();
        }

        if (!this.authService.hasRole(ROLES.ADMIN)) {
            this.commonDataFG.get('optOuts')?.disable();
        }


        this.commonDataFG.valueChanges.pipe(
            // Skip initial loading
            skip(1),
            // Only extract what matters
            map((val) => ({
                id: this.contact.id,
                firstName: val.firstName ?? ''.trim(),
                lastName: val.lastName ?? ''.trim(),
                email: val.email1 ?? ''.trim()
            } as ContactSimilarityCheckPayload)),

            // Logic Gate: (First AND Last) OR (Email)
            filter((v): v is ContactSimilarityCheckPayload =>
                Boolean((v.firstName && v.lastName) || v.email)
            ),
            // Wait for user to pause
            debounceTime(300),
            // Only fire if the fields actually changed
            distinctUntilChanged((p, c) => JSON.stringify(p) === JSON.stringify(c)),
            // Automatic cleanup
            takeUntilDestroyed(this.destroyRef)
        ).subscribe(payload => {
            this.checkDuplicateParams.set(payload);
        });

        this.supplierDataFG = this.fb.group({
            taxId: [this.contact?.supplierDetails?.taxId || ''],
            products: [],
            additionalInfo: [this.contact?.supplierDetails?.additionalInfo || ''],
            ongoingRelationship: [this.contact?.supplierDetails?.ongoingRelationship || OngoingRelationship.NOT_YET],
            supplierFeedback: [this.contact?.supplierDetails?.supplierFeedback || SupplierFeedback.NOT_YET],
            supplierEmail: [this.contact?.supplierDetails?.supplierEmail || '', Validators.email],
            website: [this.contact?.supplierDetails?.website || '', urlValidator()],
            driveFolder: [this.contact?.supplierDetails?.driveFolder || ''],
        });
    }

    initEmptyForm(): void {
        this.isSupplier.set(false);
        this.supplierDataFG.disable();
    }

    toggleSupplier(event: MatCheckboxChange): void {
        // UI sets the check state right upon click, so I have to force it back
        // to its current value before asking for confirmation
        event.source.checked = this.isSupplier();

        // Following task in Planner "when you click on "supplier" for a contact or org, 
        // clean up the "stakeholder group" field from any entry previously created"
        if (!this.isSupplier()) {
            const dialogRef = this.dialog.open(ConfirmDialogComponent, {
                data: {
                    title: 'Please confirm',
                    message: 'Creating supplier data will clear stakeholder groups.'
                }
            });
            dialogRef.afterClosed().subscribe(result => {
                if (result) {
                    this.isSupplier.set(true);
                    this.commonDataFG.get('stakeholderGroups')?.patchValue([]);
                    this.commonDataFG.get('stakeholderGroups')?.setValidators([]);
                    this.commonDataFG.get('stakeholderGroups')?.updateValueAndValidity();
                    this.supplierDataFG.enable();
                } else {
                    console.debug('no change');
                }
            });
        } else {
            const dialogRef = this.dialog.open(ConfirmDialogComponent, {
                data: {
                    title: 'Please confirm',
                    message: 'Are you sure you want to proceed? Supplier data will be cleared.'
                }
            });
            dialogRef.afterClosed().subscribe(result => {
                if (result) {
                    this.isSupplier.set(false);
                    this.commonDataFG.get('stakeholderGroups')?.setValidators(Validators.required);
                    this.commonDataFG.get('stakeholderGroups')?.updateValueAndValidity();
                    this.supplierDataFG.disable();
                    this.supplierDataFG.reset();
                } else {
                    console.debug('no change');
                }
            });
        }
    }

    get affiliationsFA() {
        return this.commonDataFG.controls['affiliationsFA'] as FormArray<FormGroup>;
    }

    get postalAddressFG(): FormGroup {
        return this.commonDataFG.get('postalAddress') as FormGroup;
    }

    get phoneNumberFG(): FormGroup {
        return this.commonDataFG.get('phoneNumber') as FormGroup;
    }

    openUrl(value: string | null) {
        console.debug('openUrl');
        if (!value) {
            return;
        }

        // Normalize missing protocol
        const url =
            value.startsWith('http://') || value.startsWith('https://')
                ? value
                : `https://${value}`;

        // Optional: check validity
        if (this.commonDataFG.get('linkedInProfile')?.valid) {
            window.open(url, '_blank');
        }
    }

    addAffiliation(a: Affiliation | undefined = undefined): void {
        const group = this.fb.group({
            id: new FormControl(a?.id ?? null),
            position: this.fb.control(a?.position ?? '', { updateOn: 'blur' }),
            organisation: new FormControl((a?.organisation ? {
                id: a?.organisation?.id ?? null,
                name: a?.organisation?.name ?? null,
                member: a?.organisation?.activeMember ?? false,
                supplier: a?.organisation?.supplier ?? false,
                icons: [
                    ...(a?.organisation?.archived ? ['archive'] : []),
                    ...(a?.organisation?.activeMember ? ['family_star'] : []),
                    ...(a?.organisation?.supplier ? ['partner_exchange'] : []),]
            } : null)),
            recordedDate: new FormControl(a?.recordedDate ?? DateTime.now().toFormat('yyyy-MM-dd')),
            primaryContact: new FormControl({ value: a?.activePrimaryContact ?? false, disabled: !a?.organisation?.activeMember }),
            invoicingContact: new FormControl({ value: a?.activeInvoicingContact ?? false, disabled: !a?.organisation?.activeMember }),
            contractContact: new FormControl({ value: a?.activeContractContact ?? false, disabled: !a?.organisation?.activeMember }),
        }, { validators: [atLeastOneFilledValidator] });

        group.get('organisation')!.valueChanges.subscribe(org => {
            console.log('organisation changed. orgid org.id affiliationid contactid ', org);

            if ((org?.id != null) && (this.contact?.id != null)) {
                // contactId is null if new contact
                // orgId is null if no organisation is selected
                this.organisationService.getContactRolesInOrganisation(org.id, this.contact.id).subscribe(v => {
                    group.get('primaryContact')?.setValue(v.primaryContact, { emitEvent: false });
                    group.get('invoicingContact')?.setValue(v.invoicingContact, { emitEvent: false });
                    group.get('contractContact')?.setValue(v.contractContact, { emitEvent: false });
                    if (org?.member) {
                        group.get('primaryContact')?.enable({ emitEvent: false });
                        group.get('invoicingContact')?.enable({ emitEvent: false });
                        group.get('contractContact')?.enable({ emitEvent: false });
                    } else {
                        group.get('primaryContact')?.disable({ emitEvent: false });
                        group.get('invoicingContact')?.disable({ emitEvent: false });
                        group.get('contractContact')?.disable({ emitEvent: false });
                    }
                })
            } else {
                if (org?.member) {
                    group.get('primaryContact')?.enable({ emitEvent: false });
                    group.get('invoicingContact')?.enable({ emitEvent: false });
                    group.get('contractContact')?.enable({ emitEvent: false });
                } else {
                    group.get('primaryContact')?.disable({ emitEvent: false });
                    group.get('invoicingContact')?.disable({ emitEvent: false });
                    group.get('contractContact')?.disable({ emitEvent: false });
                }
                group.get('primaryContact')?.setValue(false, { emitEvent: false });
                group.get('invoicingContact')?.setValue(false, { emitEvent: false });
                group.get('contractContact')?.setValue(false, { emitEvent: false });
            }

            // this is required otherwise, id may be undefined which breaks the patchValue
            const orgValue = group.get('organisation')!.value as
                {
                    id: number;
                    name: string;
                    member: boolean;
                    supplier: boolean;
                    icons: string[];
                };
            group.get('organisation')!.patchValue(orgValue, { emitEvent: false });
            group.markAsTouched();
            group.updateValueAndValidity();

        })

        this.affiliationsFA.push(group);
    }

    deleteAffiliation(i: number): void {
        console.debug('deleteAffiliation', i)
        this.affiliationsFA.removeAt(i);
    }

    cancel(): void {
        this.router.navigateByUrl(this.returnUrl);
    }

    save(): void {
        console.debug('save', this.commonDataFG.valid, this.isSupplier(), this.supplierDataFG.valid);

        if (this.commonDataFG.invalid || ((this.isSupplier() && this.supplierDataFG.invalid))) {
            console.debug('invalid forms');
            this.commonDataFG.markAllAsTouched(); // ensures Material shows errors return;
            this.supplierDataFG.markAllAsTouched();
            this._snackBar.open('❌ Please check for missing or invalid fields', 'Close', { duration: 3000 });
            return
        }

        if (this.commonDataFG.valid && ((this.isSupplier() && this.supplierDataFG.valid) || !this.isSupplier())) {
            console.debug('commonDataFG', this.commonDataFG.getRawValue())
            console.debug('supplierDataFG', this.supplierDataFG.getRawValue());
            const commonData = this.commonDataFG.getRawValue();
            const supplierData = this.supplierDataFG.getRawValue();

            // const updatedContact: Contact = {
            //     ...this.contact,
            // }
            const updatedContact: Contact = new Contact(this.contact);

            updatedContact.firstName = commonData.firstName;
            updatedContact.lastName = commonData.lastName;
            updatedContact.email1 = commonData.email1;
            updatedContact.email2 = commonData.email2;
            updatedContact.communicationType1OptIn = commonData.optOuts.communicationType1OptIn;
            updatedContact.communicationType2OptIn = commonData.optOuts.communicationType2OptIn;
            updatedContact.communicationType3OptIn = commonData.optOuts.communicationType3OptIn;
            updatedContact.communicationType4OptIn = commonData.optOuts.communicationType4OptIn;
            updatedContact.communicationType5OptIn = commonData.optOuts.communicationType5OptIn;
            updatedContact.phoneNumber = commonData.phoneNumber;
            updatedContact.postalAddress = commonData.postalAddress;
            updatedContact.linkedInProfile = commonData.linkedInProfile;
            updatedContact.linkedInStatus = commonData.linkedInStatus;
            updatedContact.scientificAdvisoryBoard = commonData.scientificAdvisoryBoard;
            updatedContact.boardOfDirectors = commonData.boardOfDirectors;
            updatedContact.generalAssembly = commonData.generalAssembly;
            updatedContact.executiveCommittee = commonData.executiveCommittee;
            updatedContact.nominationCommittee = commonData.nominationCommittee;
            updatedContact.ethicsCommittee = commonData.ethicsCommittee;
            updatedContact.additionalInfo = commonData.additionalInfo;
            updatedContact.initiatives = commonData.initiatives
            updatedContact.stakeholderGroups = commonData.stakeholderGroups
            updatedContact.topics = commonData.topics;
            updatedContact.projects = commonData.projects;
            updatedContact.events = commonData.events;
            updatedContact.affiliations = commonData.affiliationsFA;
            // const affiliations = this.affiliationsFA.getRawValue().map(a => {
            //     console.log("a = ", a);
            //     const orgSummary = this.organisations.value().find(o => o.id === a['organisation']);
            //     return {
            //         id: a['id'],
            //         position: a['position'],
            //         recordedDate: a['recordedDate'],
            //         primaryContact: a['primaryContact'],
            //         invoicingContact: a['invoicingContact'],
            //         organisation: orgSummary ?? null
            //     };
            // });
            // console.log(affiliations);
            // updatedContact.affiliations = affiliations;


            if (this.isSupplier()) {
                updatedContact.supplierDetails = supplierData;
            } else {
                updatedContact.supplierDetails = null;
            }

            console.debug('contact', updatedContact);

            if (this.mode === 'Create') {
                this.contactService.create(updatedContact).subscribe({
                    next: () => {
                        this.router.navigateByUrl(this.returnUrl,
                            {
                                state: { message: '✅ Contact successfully added' }
                            }
                        );
                    },
                    error: (err) => {
                        console.error('error adding contact', err)
                        this._snackBar.open('❌ Error adding contact', 'Close', { duration: 3000 });
                    }
                })
            } else {
                this.contactService.update(updatedContact).subscribe({
                    next: () => {
                        this.router.navigateByUrl(this.returnUrl,
                            {
                                state: { message: '✅ Contact successfully updated' }
                            }
                        );
                    },
                    error: (err) => {
                        console.error('error updating contact', err)
                        this._snackBar.open('❌ Error updating contact', 'Close', { duration: 3000 });
                    }
                })
            }
        }
    }

    delete(): void {
        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
            data: {
                title: 'Delete contact',
                message: 'Are you sure you want to delete this contact? It will also be removed from any organisation is was affiliated with.'
            }
        });
        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.contactService.delete(this.contact!.id).subscribe({
                    next: () => {
                        this.router.navigateByUrl(this.returnUrl,
                            {
                                state: { message: '✅ Contact deleted' }
                            }
                        );
                    },
                    error: (err) => {
                        console.error('error deleting contact', err)
                        this._snackBar.open('❌ Error deleting contact', 'Close', { duration: 3000 });
                    }
                })
            }
        });
    }

    toggleArchive(): void {
        if (!this.contact.archived && this.contact.affiliations.some(a => (a.activePrimaryContact || a.activeInvoicingContact ||
            a.activeContractContact || a.futurePrimaryContact || a.futureInvoicingContact || a.futureContractContact))) {
            this._snackBar.open('❌ Contact is an active or future representative for a EUFIC member', 'Close', { duration: 3000 });
            return;
        }

        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
            data: {
                title: `${this.contact.archived ? 'Un-archive' : 'Archive'} contact`,
                message: `Are you sure you want to ${this.contact.archived ? 'un-archive' : 'archive'} this contact?`
            }
        });
        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.contactService.setArchivedStatus(this.contact!.id, !this.contact!.archived).subscribe({
                    next: () => {
                        this.router.navigateByUrl(this.returnUrl,
                            {
                                state: { message: `✅ Contact ${this.contact.archived ? 'un-archived' : 'archived'}` }
                            }
                        );
                    },
                    error: (err) => {
                        console.error(`error ${this.contact.archived ? 'un-archiving' : 'archiving'} contact`, err)
                        this._snackBar.open(`❌ Error ${this.contact.archived ? 'un-archiving' : 'archiving'} contact`, 'Close', { duration: 3000 });
                    }
                })
            }
        });
    }

    onAddInitiative(value: string) {
        console.debug('adding initiative', value, 'to list')
        this.initiativeService.create({ id: 0, name: value, }).subscribe({
            next: (item) => {
                this.initiatives.reload();
                const current = this.commonDataFG.get('initiatives')!.value ?? [];
                console.debug('current', current)
                this.commonDataFG.controls['initiatives'].setValue([...current, item]);
                console.log('✅ Initiative added', item)
                this._snackBar.open('✅ Initiative added', 'Close', { duration: 3000 });
            },
            error: (err) => {
                console.error('error adding initiative', err);
                this._snackBar.open('❌ Error adding initiative', 'Close', { duration: 3000 });
            }
        })
    }

    onAddOrganisation(value: string) {
        console.debug('adding organisation', value, 'to list')
        this.organisationService.createMinimal(value).subscribe({
            next: (item) => {
                this.organisations.reload();
                // console.log('✅ Initiative added')
                this._snackBar.open('✅ Organisation added. Please do not forget to fill-in the missing info in the organisations page', 'Close', { duration: 0 });
            },
            error: (err) => {
                console.error('error adding organisation', err);
                this._snackBar.open('❌ Error adding organisation', 'Close', { duration: 3000 });
            }

        })
    }

    onOptOutChanged(event: MatCheckboxChange) {
        if (event.checked) {
            // set the date to today
            console.debug('setting');
            this.commonDataFG.get('optOutDate')?.setValue(DateTime.now().toFormat('d MMM yyyy'));
        } else {
            // clean the date
            console.debug('unsetting');

            this.commonDataFG.get('optOutDate')?.setValue('N/A');
        }
    }

    togglePrimaryContact(i: number) {
        const selected = this.affiliationsFA.at(i).get('primaryContact')?.value;
        const orgName = this.affiliationsFA.at(i).get('organisation')?.value.name
        console.debug('orgName', orgName);
        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
            data: {
                title: 'Update primary contact',
                message: (selected ? 'Are you sure you want to remove this contact as primary contact for ' + orgName + '?' :
                    'Are you sure you want to add this contact as primary contact for ' + orgName + '?')
            }
        });
        dialogRef.afterClosed().subscribe(result => {
            console.debug('closed')
        })
    }

    toggleInvoicingContact(i: number) {
        const selected = this.affiliationsFA.at(i).get('invoicingContact')?.value;
        const orgName = this.affiliationsFA.at(i).get('organisation')?.value.name

        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
            data: {
                title: 'Update invoicing contact',
                message: (selected ? 'Are you sure you want to remove this contact as invoicing contact for ' + orgName + '?' :
                    'Are you sure you want to add this contact as invoicing contact for ' + orgName + '?')
            }
        });
        dialogRef.afterClosed().subscribe(result => {
            console.debug('closed')
        })
    }

}

